import { type ReactNode } from "react";
import { Button, Popover, PopoverAnchor, PopoverContent } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";

export interface DeleteEventWarningPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isDeleting?: boolean;
  /** The element the warning anchors to (e.g. the delete button). */
  children: ReactNode;
}

export const DeleteEventWarningPopover = ({
  open,
  onOpenChange,
  onConfirm,
  isDeleting,
  children,
}: DeleteEventWarningPopoverProps) => {
  const { t } = useTranslation("calendar");

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>{children}</PopoverAnchor>
      <PopoverContent align="end" className="w-80 space-y-3" side="top">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 text-destructive" />
          <div className="space-y-1">
            <p className="text-sm font-medium">{t("Delete Event")}</p>
            <p className="text-sm text-muted-foreground">
              {t("This will permanently delete the event. This action cannot be undone.")}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t("Cancel")}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={isDeleting}
            data-testid="event-details-delete-confirm"
            onClick={onConfirm}
          >
            {t("Delete Event")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
